import { useMemo, useState } from "react";
import { AxiosError } from "axios";

import { useCheckOut, useShiftEvents } from "@/features/shift-events/model/useShiftEvents";
import { useShift } from "@/features/shifts/model/useShifts";
import { Button } from "@/shared/ui";

interface Props {
  shiftId: string;
  onSubmitted?: () => void;
  /** Dev-only: skip GPS and submit Test Diner coords to bypass the geofence. */
  devOverrideCoords?: { gps_lat: number; gps_lng: number };
}

function diffHours(checkInIso: string): number {
  const checkIn = new Date(checkInIso).getTime();
  const now = Date.now();
  const ms = Math.max(0, now - checkIn);
  return ms / 1000 / 3600;
}

function fmtHours(hours: number): string {
  const totalMin = Math.round(hours * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}

export function CheckOutSummary({ shiftId, onSubmitted, devOverrideCoords }: Props) {
  const { data: shift } = useShift(shiftId);
  const { data: events } = useShiftEvents(shiftId);
  const checkOut = useCheckOut(shiftId);

  const checkIn = events?.find((e) => e.event_type === "CHECK_IN");
  const hours = useMemo(() => (checkIn ? diffHours(checkIn.created_at) : 0), [checkIn]);
  const payRate = shift?.pay_rate ? Number(shift.pay_rate) : 0;
  const estPay = (hours * payRate).toFixed(2);

  // Note is optional and capped at 256 chars per the UX doc.
  const [note, setNote] = useState("");

  const errMsg = checkOut.error
    ? checkOut.error instanceof AxiosError
      ? (checkOut.error.response?.data?.detail ?? "Couldn't check out")
      : checkOut.error.message
    : null;

  const submit = () => {
    // Note isn't currently persisted on the backend (no field on the event).
    // We submit the GPS-bearing checkout; backend creates the timesheet.
    // TODO when we add a note field to ShiftEvent, pass `note` here.
    void note;
    checkOut.mutate(devOverrideCoords, { onSuccess: onSubmitted });
  };

  if (!checkIn) return null;

  return (
    <div className="rounded-lg border border-border bg-surface p-5 shadow-card space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-copy">Ready to check out?</h3>
        <p className="mt-1 text-sm text-copy-muted">
          We'll send these hours to the venue for review. You can leave right after.
        </p>
      </div>

      <dl className="grid grid-cols-3 gap-3 text-sm">
        <div>
          <dt className="text-copy-muted">Time worked</dt>
          <dd className="font-semibold tabular-nums text-copy">{fmtHours(hours)}</dd>
        </div>
        <div>
          <dt className="text-copy-muted">Rate</dt>
          <dd className="font-semibold tabular-nums text-copy">${payRate.toFixed(2)} / hr</dd>
        </div>
        <div>
          <dt className="text-copy-muted">Est. pay</dt>
          <dd className="font-semibold tabular-nums text-copy">${estPay}</dd>
        </div>
      </dl>

      <div>
        <label htmlFor="checkout-note" className="text-xs font-medium uppercase tracking-wider text-copy-muted">
          Anything to flag? (optional)
        </label>
        <textarea
          id="checkout-note"
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 256))}
          rows={2}
          maxLength={256}
          placeholder="e.g., closed late because of a slow last table"
          className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-copy placeholder:text-copy-subtle focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <p className="mt-1 text-xs text-copy-subtle">{note.length} / 256</p>
      </div>

      {errMsg && <p className="text-sm text-destructive">{errMsg}</p>}

      <Button
        onClick={submit}
        disabled={checkOut.isPending}
        className="w-full"
        size="lg"
      >
        {checkOut.isPending ? "Sending…" : "Send to venue"}
      </Button>
    </div>
  );
}
